import { DeleteOutline } from '@mui/icons-material';
import { Button, Card, Col, Form, Input, Row, Select, Switch, Typography } from 'antd';
import React, { useState } from 'react';
import { BandDetails, IdefaultVendor } from './Vendor';

interface VendorBandInfoCollapsibleProps {
  vendorDetails: IdefaultVendor;
  onAddBand: (bandDetails: BandDetails) => void;
  onDeleteBand: (index: number) => void;
}

interface BandDetailsFormProps {
  onAddBand: (bandDetails: BandDetails) => void;
  onCancel: () => void;
}

const BandKeyNames: Record<string, string> = {
  start_frequency: 'Frequency Range',
  bandwidth: 'Bandwidth',
  cbrs_support: 'CBRS Support',
  cbrs_category: 'CBRS Category',
  afc_support: 'AFC Support',
  six_ghz_device_category: '6 GHz Device Category',
  six_ghz_device_type: '6 GHz Device Type',
};

const BandValueNames: Record<string, string> = {
  '2_4g': '2.4GHz',
  '3_5g': '3.5GHz',
  '5_g': '5GHz',
  '6_g': '6GHz',
  primary: 'Primary',
  secondary: 'Secondary',
  A: 'Category A',
  B: 'Category B',
  '6_sd': '6 SD',
  '6_fc': '6 FC',
  Composite: 'Composite',
  low_power_indicator: 'LPI',
  standard_power: 'Standard Power',
};

type FieldType = {
  band_name?: string;
  band_type?: string;
  start_frequency?: number;
  end_frequency?: number;
  bandwidth?: number;
  cbrs_support?: boolean;
  afc_support?: boolean;
  cbrs_category?: string;
  six_ghz_device_category?: string;
  six_ghz_device_type?: string;
};

const VendorBandInfoCollapsible: React.FC<VendorBandInfoCollapsibleProps> = ({ vendorDetails, onAddBand, onDeleteBand }) => {
  const [bandDetailsFormVisible, setBandDetailsFormVisible] = useState(false);

  const handleAddBand = (bandDetails: BandDetails) => {
    setBandDetailsFormVisible(false);
    onAddBand(bandDetails);
  };
  const handleCancel = () => {
    setBandDetailsFormVisible(false);
  };

  const getBandCardContent = (key: string, value: any, band: BandDetails) => {
    if (['id', 'band_name', 'band_type', 'end_frequency'].includes(key)) {
      return null;
    }
    if (key === 'cbrs_category' && band.cbrs_support) {
      return (
        <Row key={key}>
          <Col span={10}>
            <Typography.Text>{BandKeyNames[key]}</Typography.Text>
          </Col>
          <Col span={14}>
            <Typography.Text type="secondary">{BandValueNames[value]}</Typography.Text>
          </Col>
        </Row>
      );
    }
    if (['six_ghz_device_category', 'six_ghz_device_type'].includes(key) && band.afc_support) {
      return (
        <Row key={key}>
          <Col span={10}>
            <Typography.Text>{BandKeyNames[key]}</Typography.Text>
          </Col>
          <Col span={14}>
            <Typography.Text type="secondary">{BandValueNames[value]}</Typography.Text>
          </Col>
        </Row>
      );
    }
    if (key === 'cbrs_support' || key === 'afc_support') {
      return (
        <Row key={key}>
          <Col span={10}>
            <Typography.Text>{BandKeyNames[key]}</Typography.Text>
          </Col>
          <Col span={14}>
            <Typography.Text type="secondary">{value ? 'Yes' : 'No'}</Typography.Text>
          </Col>
        </Row>
      );
    }
    if (key === 'bandwidth') {
      return (
        <Row key={key}>
          <Col span={10}>
            <Typography.Text>{BandKeyNames[key]}</Typography.Text>
          </Col>
          <Col span={14}>
            <Typography.Text type="secondary">{value} MHz</Typography.Text>
          </Col>
        </Row>
      );
    }
    if (key === 'start_frequency') {
      return (
        <Row key={key}>
          <Col span={10}>
            <Typography.Text>{BandKeyNames[key]}</Typography.Text>
          </Col>
          <Col span={14}>
            <Typography.Text type="secondary">{`${value} - ${band.end_frequency} MHz`}</Typography.Text>
          </Col>
        </Row>
      );
    }

    return null;
  };

  return (
    <div>
      {bandDetailsFormVisible ? (
        <BandDetailsForm onAddBand={handleAddBand} onCancel={handleCancel} />
      ) : (
        <div className="d-flex flex-column gap-2">
          <Button size="small" className="mb-2 align-self-start" type="primary" onClick={() => setBandDetailsFormVisible(true)}>
            Add Band
          </Button>
          {vendorDetails?.bands?.map((band: BandDetails, index: number) => (
            <Card key={index}>
              <div className="d-flex flex-column gap-2">
                <div className="d-flex flex-row justify-content-between align-items-center">
                  <Typography.Title level={5}>
                    {BandValueNames[band.band_name || '']} - {BandValueNames[band.band_type || '']}
                  </Typography.Title>
                  <Button size="small" type="link" onClick={() => onDeleteBand(index)}>
                    <DeleteOutline fontSize="small" style={{ color: 'red' }} />
                  </Button>
                </div>
                {Object.entries(band).map(([key, value]) => getBandCardContent(key, value, band))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const BandDetailsForm: React.FC<BandDetailsFormProps> = ({ onAddBand, onCancel }) => {
  const [form] = Form.useForm<FieldType>();
  const [showCBRS, setShowCBRS] = useState(false);
  const [showAFC, setShowAFC] = useState(false);

  const handleChange = (key: string, value: any) => {
    form.setFieldsValue({ [key]: value });
  };
  const handleAddBand = () => {
    form
      .validateFields()
      .then((values) => {
        onAddBand(values);
      })
      .catch((error) => {
        console.log(error);
      });
  };
  const handleCancel = () => {
    form.resetFields();
    setShowCBRS(false);
    setShowAFC(false);
    onCancel();
  };
  return (
    <Card>
      <Form
        form={form}
        name="basic"
        labelCol={{ span: 10 }}
        wrapperCol={{ span: 24 }}
        initialValues={{
          band_name: '',
          band_type: '',
          start_frequency: null,
          end_frequency: null,
          bandwidth: null,
          cbrs_support: false,
          cbrs_category: '',
          afc_support: false,
          six_ghz_device_category: '',
          six_ghz_device_type: '',
        }}
        autoComplete="off">
        <Form.Item<FieldType> label="Band Name" name="band_name" rules={[{ required: true, message: 'Please provide band name!' }]}>
          <Select
            getPopupContainer={(triggerNode) => {
              return triggerNode.parentNode;
            }}
            options={[
              { label: '2.4GHz', value: '2_4g' },
              { label: '3.5GHz', value: '3_5g' },
              { label: '5GHz', value: '5_g' },
              { label: '6GHz', value: '6_g' },
            ]}
            onChange={(value) => handleChange('band_name', value)}
          />
        </Form.Item>
        <Form.Item<FieldType> label="Band Type" name="band_type" rules={[{ required: true, message: 'Please provide band type!' }]}>
          <Select
            getPopupContainer={(triggerNode) => {
              return triggerNode.parentNode;
            }}
            options={[
              { label: 'Primary', value: 'primary' },
              { label: 'Secondary', value: 'secondary' },
            ]}
            onChange={(value) => handleChange('band_type', value)}
          />
        </Form.Item>
        <Form.Item<FieldType> label="Start Frequency" name="start_frequency" rules={[{ required: true, message: 'Please provide start frequency!' }]}>
          <Input type="number" addonAfter="MHz" onChange={(e) => handleChange('start_frequency', e.target.value)} />
        </Form.Item>
        <Form.Item<FieldType> label="End Frequency" name="end_frequency" rules={[{ required: true, message: 'Please provide end frequency!' }]}>
          <Input type="number" addonAfter="MHz" onChange={(e) => handleChange('end_frequency', e.target.value)} />
        </Form.Item>
        <Form.Item<FieldType> label="Bandwidth" name="bandwidth" rules={[{ required: true, message: 'Please provide bandwidth!' }]}>
          <Input type="number" addonAfter="MHz" onChange={(e) => handleChange('bandwidth', e.target.value)} />
        </Form.Item>
        <Form.Item<FieldType> label="CBRS Support" name="cbrs_support" rules={[{ required: false }]}>
          <Switch
            checkedChildren="Yes"
            unCheckedChildren="No"
            defaultChecked={false}
            onChange={(e) => {
              handleChange('cbrs_support', e);
              setShowCBRS(e);
            }}
          />
        </Form.Item>

        <Form.Item<FieldType> hidden={!showCBRS} label="CBRS Category" name="cbrs_category" rules={[{ required: false }]}>
          <Select
            getPopupContainer={(triggerNode) => {
              return triggerNode.parentNode;
            }}
            options={[
              { label: 'Category A', value: 'A' },
              { label: 'Category B', value: 'B' },
            ]}
            onChange={(value: any) => handleChange('cbrs_category', value)}
          />
        </Form.Item>
        <Form.Item<FieldType> label="AFC Support" name="afc_support" rules={[{ required: false }]}>
          <Switch
            checkedChildren="Yes"
            unCheckedChildren="No"
            defaultChecked={false}
            onChange={(e) => {
              handleChange('afc_support', e);
              setShowAFC(e);
            }}
          />
        </Form.Item>
        <Form.Item<FieldType> hidden={!showAFC} label="6 GHz Device Category" name="six_ghz_device_category" rules={[{ required: false }]}>
          <Select
            getPopupContainer={(triggerNode) => {
              return triggerNode.parentNode;
            }}
            options={[
              { label: '6 SD', value: '6_sd' },
              { label: '6 FC', value: '6_fc' },
            ]}
            onChange={(value: any) => handleChange('six_ghz_device_category', value)}
          />
        </Form.Item>
        <Form.Item<FieldType> hidden={!showAFC} label="6 GHz Device Type" name="six_ghz_device_type" rules={[{ required: false }]}>
          <Select
            getPopupContainer={(triggerNode) => {
              return triggerNode.parentNode;
            }}
            options={[
              { label: 'Composite', value: 'Composite' },
              { label: 'LPI', value: 'low_power_indicator' },
              { label: 'Standard Power', value: 'standard_power' },
            ]}
            onChange={(value: any) => handleChange('six_ghz_device_type', value)}
          />
        </Form.Item>
        <Form.Item className="text-end">
          <Button size="small" className="me-2" type="default" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="small" type="primary" onClick={handleAddBand}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default VendorBandInfoCollapsible;
